import { catalogResponseTransform, catalogIndexPublished, catalogRenderCommitted } from './services/catalogPerformance';
import { loadSelectedModelRepairs, projectPublicRepair } from './services/selectedModelRepairs';
import { loadProgressiveRepairCatalog, type CatalogRow } from './services/progressiveRepairCatalog';
import { canonicalAppointmentIdentity } from './domain/appointmentSelection';
import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef, Suspense } from 'react';
import axios from 'axios';
import { Page, Brand, PhoneModel, RepairOption, ServiceOfferOption, Order, AppContextType, UnifiedCartItem, Part, CustomThemeConfig, HeroSlide, Appointment, QuoteRequest, FooterInfoData, BlogPost, PromotionSettings, BuybackRequest, BackendProduct, AppNotification, SellPhoneFormData, GalleryImage } from './types';
import { PARTS_DATA } from './data/partsData';
import { FOOTER_DATA } from './data/footerData';
import { FIND_MY_MODEL_DATA } from './data/findMyModelData';
import { getTranslation } from './utils/translations';
import { lazyRetry } from './utils/lazyRetry';
import {
    fetchCanonicalPageContext,
    fetchResolvedPageContext,
    isResolvedPageContext,
    navigateResolvedPage,
    projectBrowserStatePath,
    readServerResolvedPageContext,
    type CanonicalEntityIds,
    type PageArchetype,
    type ResolvedPageContext
} from './domain/resolvedPageContext';
import { readPublicSsrBootstrap, readServerSemanticPage } from './domain/publicSsrBootstrap';
import { useDeferredHeroVideo } from './hooks/useDeferredHeroVideo';

const PublicPageReadyBoundary: React.FC<React.PropsWithChildren> = ({ children }) => {
    useLayoutEffect(() => {
        document.documentElement.dataset.publicAppReady = 'true';
        document.dispatchEvent(new Event('PUBLIC_APP_READY'));
    }, []);
    return <>{children}</>;
};

import HomePage from './pages/HomePage';
import AdminRedirect from './components/AdminRedirect';
const RepairsPage = lazyRetry(() => import('./pages/RepairsPage'));
const UnsubscribePage = lazyRetry(() => import('./pages/UnsubscribePage'));
const AccessoriesPage = lazyRetry(() => import('./pages/AccessoriesPage'));
const ImeiPage = lazyRetry(() => import('./pages/ImeiPage'));
const ContactPage = lazyRetry(() => import('./pages/ContactPage'));
const FindMyModelPage = lazyRetry(() => import('./pages/FindMyModelPage'));
const BuyPhonePage = lazyRetry(() => import('./pages/BuyPhonePage'));
const SellPhonePage = lazyRetry(() => import('./pages/SellPhonePage'));
const ProductDetailPage = lazyRetry(() => import('./pages/ProductDetailPage'));
const AccessoryDetailPage = lazyRetry(() => import('./pages/AccessoryDetailPage'));
const BlogPage = lazyRetry(() => import('./pages/BlogPage'));
const TermsPage = lazyRetry(() => import('./pages/TermsPage'));
const DevicesPage = lazyRetry(() => import('./pages/DevicesPage'));
const InstallSetup = lazyRetry(() => import('./pages/InstallSetup'));

import Navbar from './components/Navbar';
import PromoBanner from './components/PromoBanner';
import MetaTags from './components/MetaTags';
import { Toast } from './components/ui/Toast';
import { AppContext } from './context/AppContext';
import { mapPublicProductCharacteristics } from './utils/productCharacteristics';

const FooterInfo = lazyRetry(() => import('./components/FooterInfo'));
const AiChatbot = lazyRetry(() => import('./components/AiChatbot'));
const PromotionPopup = lazyRetry(() => import('./components/PromotionPopup'));
const LogoDesigner = lazyRetry(() => import('./components/LogoDesigner'));
const UnifiedCartDrawer = lazyRetry(() => import('./components/CartComponents').then(m => ({ default: m.UnifiedCartDrawer })));
const WishlistManager = lazyRetry(() => import('./components/WishlistManager').then(m => ({ default: m.WishlistManager })));

const DeferredFooterInfo: React.FC = () => {
    const sentinelRef = useRef<HTMLDivElement>(null);
    const [shouldRender, setShouldRender] = useState(false);

    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel || shouldRender) return;
        if (!('IntersectionObserver' in window)) {
            setShouldRender(true);
            return;
        }
        const observer = new IntersectionObserver((entries) => {
            if (entries.some(entry => entry.isIntersecting)) {
                setShouldRender(true);
                observer.disconnect();
            }
        }, { rootMargin: '800px 0px' });
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [shouldRender]);

    return (
        <div ref={sentinelRef} data-deferred-footer-boundary>
            {shouldRender && (
                <Suspense fallback={null}>
                    <FooterInfo />
                </Suspense>
            )}
        </div>
    );
};

import { TrackingProvider, sendPublicTelemetry } from './components/TrackingProvider';
import { LazyMotion } from 'motion/react';

// Secure fallback for admin path
const DEFAULT_ADMIN_PATH = '/admin-gestion-Z9!wX@8k$qP';
const STABLE_SYSTEM_FONT_STACK = 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif';
const GOVERNED_THEME_FONTS: Record<string, string> = {
    'Space Grotesk': 'Space+Grotesk:wght@300..700',
    Outfit: 'Outfit:wght@300..900',
    'JetBrains Mono': 'JetBrains+Mono:wght@400..800',
    'Playfair Display': 'Playfair+Display:ital,wght@0,400..900;1,400..900',
    'Plus Jakarta Sans': 'Plus+Jakarta+Sans:wght@300..800',
    Montserrat: 'Montserrat:wght@300..900',
    Poppins: 'Poppins:wght@300..900',
    Orbitron: 'Orbitron:wght@400..900'
};

const serverResolvedPageContext = readServerResolvedPageContext();
const serverPublicSsrBootstrap = readPublicSsrBootstrap();
const serverRepairIdentity = readServerSemanticPage(serverResolvedPageContext)?.repairIdentity;

const pageFromResolvedContext = (context: ResolvedPageContext | null): Page => {
    if (!context) return Page.Home;
    switch (context.pageArchetype) {
        case 'REPAIR_ROOT':
        case 'REPAIR_BRAND':
        case 'REPAIR_MODEL':
        case 'REPAIR_MODEL_SERVICE': return Page.Repairs;
        case 'PHONE_CATALOG_ROOT': return Page.BuyPhone;
        case 'ACCESSORY_CATALOG_ROOT': return Page.Accessories;
        case 'PRODUCT_PHONE': return Page.ProductDetail;
        case 'PRODUCT_ACCESSORY':
        case 'PRODUCT_OTHER': return Page.AccessoryDetail;
        case 'BLOG_ROOT':
        case 'BLOG_ARTICLE': return Page.Blog;
        case 'DEVICE_ROOT':
        case 'DEVICE_CATEGORY':
        case 'DEVICE_BRAND':
        case 'DEVICE_MODEL': return Page.Devices;
        case 'RACHAT': return Page.SellPhone;
        case 'CONTACT':
        case 'APPOINTMENT_STATE': return Page.Contact;
        case 'STATIC': {
            const slug = context.slugs.static;
            if (slug === 'imei') return Page.ImeiVerification;
            if (slug === 'aide') return Page.FindMyModel;
            if (slug === 'terms') return Page.Terms;
            if (slug === 'setup') return Page.Setup;
            if (slug === 'unsubscribe') return Page.Unsubscribe;
            return Page.Home;
        }
        default: return Page.Home;
    }
};

// Animation features remain a separate noncritical chunk, but their download
// starts immediately. Primary content uses animation-owned opacity values, so
// waiting for a customer gesture here would leave a CLIENT_READY page invisible.
const motionFeaturesPromise = import('./motionFeatures').then(module => {
    document.documentElement.dataset.publicMotionReady = 'true';
    document.dispatchEvent(new Event('PUBLIC_MOTION_READY'));
    return module.default;
});
const loadMotionFeatures = () => motionFeaturesPromise;

export const AppWrapper: React.FC = () => {
    return (
        <LazyMotion features={loadMotionFeatures} strict>
            <TrackingProvider>
                <App />
            </TrackingProvider>
        </LazyMotion>
    );
};

const App: React.FC = () => {
    const shouldLoadAuxiliaryUi = useDeferredHeroVideo(true);
    const [secretPath, setSecretPath] = useState<string>(() => {
        try {
            const saved = localStorage.getItem('mobilbo_admin_path');
            if (saved) return saved;
        } catch (e) {
            console.error("[MOBILBO] Failed to read admin path from localStorage:", e);
        }
        return DEFAULT_ADMIN_PATH;
    });
    const [resolvedPageContext, setResolvedPageContext] = useState<ResolvedPageContext | null>(serverResolvedPageContext);
    const [page, setPage] = useState<Page>(() => {
        const path = window.location.pathname.toLowerCase();
        if (
            (secretPath && path === secretPath.toLowerCase()) || 
                  path === '/admin' ||
path === DEFAULT_ADMIN_PATH.toLowerCase()
        ) return Page.Admin;
        return pageFromResolvedContext(serverResolvedPageContext);
    });
    const [isConfigLoaded, setIsConfigLoaded] = useState(Boolean(serverPublicSsrBootstrap));
    const [apiError, setApiError] = useState<string | null>(null);

    // Language state and translations
    const [language, setLanguageState] = useState<'fr' | 'de' | 'en'>(() => {
        try {
            const saved = localStorage.getItem('mobilbo_language');
            if (saved === 'fr' || saved === 'de' || saved === 'en') {
                return saved;
            }
        } catch (e) {
            console.error("[MOBILBO] Failed to read language from localStorage:", e);
        }
        return 'fr';
    });

    const setLanguage = useCallback((lang: 'fr' | 'de' | 'en') => {
        setLanguageState(lang);
        try {
            localStorage.setItem('mobilbo_language', lang);
        } catch (e) {
            console.error("[MOBILBO] Failed to write language to localStorage:", e);
        }
    }, []);

    const t = useCallback((key: string, defaultText?: string) => {
        return getTranslation(language, key, defaultText);
    }, [language]);

    // Context States
    const [repairData, setRepairData] = useState<Brand[]>(() => serverRepairIdentity ? [serverRepairIdentity.brand] : []);
    const [selectedBrand, setSelectedBrand] = useState<Brand | null>(serverRepairIdentity?.brand || null);
    const [selectedModel, setSelectedModel] = useState<PhoneModel | null>(serverRepairIdentity?.model || null);
    const [selectedSeries, setSelectedSeries] = useState<string | null>(null);
    const [selectedRepairs, setSelectedRepairs] = useState<RepairOption[]>([]);
    const [cart, setCart] = useState<UnifiedCartItem[]>([]);
    const [isCartOpen, setIsCartOpen] = useState(false);
    const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
    const [orders, setOrders] = useState<Order[]>([]);
    const [parts, setParts] = useState<Part[]>(() => serverPublicSsrBootstrap?.productCatalog || PARTS_DATA);

  React.useEffect(() => {
    let cancelled = false;
    fetch('/api/public/products')
      .then(response => response.ok ? response.json() : [])
      .then((products: Part[]) => {
        if (!cancelled && Array.isArray(products)) setParts(products);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const [, setIsProductCatalogLoaded] = useState(false);
    const [savedThemes, setSavedThemes] = useState<CustomThemeConfig[]>([]);
    const [activeThemeConfig, setActiveThemeConfig] = useState<CustomThemeConfig>({ id: 'default', name: 'Crystal Premium', fontFamily: 'Inter', primaryColor: '#3b82f6', layoutType: 'crystal', isActive: true });
    const [selectedPhoneForPurchase, setSelectedPhoneForPurchase] = useState<Part | null>(null);
    const [selectedPartForPurchase, setSelectedPartForPurchase] = useState<Part | null>(null);
    const [selectedBlogPost, setSelectedBlogPost] = useState<BlogPost | null>(null);
    const [showcaseProducts, setShowcaseProducts] = useState<string[]>([]);
    const [sellPhoneStep, setSellPhoneStep] = useState(1);
    const [sellPhoneFormData, setSellPhoneFormData] = useState<SellPhoneFormData>({
        brand: '',
        model: '',
        capacity: '',
        condition_rating: 'Comme neuf',
        battery_health: '100',
        has_box: false,
        has_charger: false,
        has_invoice: false,
        issue_face_id: false,
        issue_camera: false,
        issue_icloud: false,
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        customer_address: '',
        customer_iban: '',
        customer_twint: '',
        agreedToTerms: false,
        subscribedToNewsletter: false
    });
    const [sharedFormData, setSharedFormData] = useState<Record<string, unknown> | null>(() => {
        try {
            const saved = localStorage.getItem('sharedFormData');
            return saved ? JSON.parse(saved) : null;
        } catch (e) {
            console.error("[MOBILBO] Failed to parse sharedFormData from localStorage:", e);
            return null;
        }
    });

    useEffect(() => {
        if (sharedFormData) {
            localStorage.setItem('sharedFormData', JSON.stringify(sharedFormData));
        } else {
            localStorage.removeItem('sharedFormData');
        }
    }, [sharedFormData]);
    const [heroSlides, setHeroSlides] = useState<HeroSlide[]>([{ id: '1', imageUrl: '', title: '', subtitle: 'votre région.' }]);
    const [promotionSettings, setPromotionSettings] = useState<PromotionSettings>({ isEnabled: false, title: '', description: '', image: '', discountCode: '', discountPercent: 0, buttonLabel: '', targetPage: Page.Contact, displayOnPages: [Page.Home], startDate: '', endDate: '', showOncePerSession: true, displayDelay: 2, overlayBlur: true, isDismissible: true });
    const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
    const [appointments, setAppointments] = useState<Appointment[]>([]);
    const [quoteRequests, setQuoteRequests] = useState<QuoteRequest[]>([]);
    const [buybackRequests, setBuybackRequests] = useState<BuybackRequest[]>([]);
    const [footerInfo, setFooterInfo] = useState<FooterInfoData>(() => ({
        ...FOOTER_DATA,
        ...(serverPublicSsrBootstrap?.businessInformation || {})
    }));
    const [isAdmin, setIsAdmin] = useState(() => {
        const token = localStorage.getItem('mobilbo_token');
        if (token && token !== 'null' && token !== 'undefined') {
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            axios.defaults.headers.common['X-Auth-Token'] = token;
            return true;
        }
        return false;
    });

    // URL Routing Handler - Setup and Unsubscribe handled in initializer
    // This effect can be removed or kept for dynamic updates if needed, 
    // but the linter warns about synchronous setState in effect.
    // For now, removing the initial mount sync checks.
    useEffect(() => {
        // Only handles window resize or other events if needed
    }, []);

    // Global scroll to top on page change
    useEffect(() => {
        window.scrollTo(0, 0);
        if (page !== Page.Blog) {
 setSelectedBlogPost(null);
 }
 // Product category resolution is asynchronous for /shop/:slug.
    // Preserve both selections while moving between detail archetypes.
    if (page !== Page.ProductDetail && page !== Page.AccessoryDetail) {
      setSelectedPhoneForPurchase(null);
      setSelectedPartForPurchase(null);
    }
  }, [page]);

    // Notifications state
    const [notifications, setNotifications] = useState<AppNotification[]>([]);

    const commitResolvedPageContext = useCallback((context: ResolvedPageContext) => {
        setResolvedPageContext(context);
        setPage(pageFromResolvedContext(context));
        setApiError(null);
    }, []);

    const resolveBrowserLocation = useCallback(async (stateValue?: unknown) => {
        const path = window.location.pathname.toLowerCase();
        const activeAdminPath = (secretPath || DEFAULT_ADMIN_PATH).toLowerCase();
        if (path === '/admin' || path === activeAdminPath || path === DEFAULT_ADMIN_PATH.toLowerCase()) {
            setPage(Page.Admin);
            return;
        }

        if (path === '/blog' || path.startsWith('/blog/')) {
      setResolvedPageContext(null);
      setApiError(null);
      setPage(Page.Blog);
      return;
    }

    const stateContext = stateValue && typeof stateValue === 'object'
            ? (stateValue as { resolvedPageContext?: unknown }).resolvedPageContext
            : null;
        const trustedState = isResolvedPageContext(stateContext)
            && stateContext.canonicalPath === window.location.pathname
            && (!serverResolvedPageContext || stateContext.siteId === serverResolvedPageContext.siteId)
            ? stateContext
            : null;
        try {
            const hashContact = /^#contact(?:\?|$)/iu.test(window.location.hash)
                ? `/contact${window.location.hash.includes('?')
                    ? `?${window.location.hash.split('?').slice(1).join('?')}`
                    : window.location.search}`
                : null;
            const context = trustedState || await fetchResolvedPageContext(
                hashContact || `${window.location.pathname}${window.location.search}`
            );
            // The HTTP fallback used by older deployments rewrites a deep link
            // such as /contact?... to /?...#contact.  The resolver is asked
            // about the reconstructed canonical path, so it can legitimately
            // report redirectRequired=false even though the browser is still
            // displaying the legacy fallback URL.  Always restore the exact
            // canonical appointment URL when that fallback is detected.
            if (hashContact || (context.redirectRequired && context.canonicalPath !== window.location.pathname)) {
                window.history.replaceState(
                    { resolvedPageContext: context },
                    '',
                    hashContact || projectBrowserStatePath(context)
                );
            }
            commitResolvedPageContext(context);
        } catch (error) {
            const resolutionStatus = (error as { response?: { status?: number } })?.response?.status;
      const expectedMissingResource = resolutionStatus === 400 || resolutionStatus === 404 || (error instanceof Error && /ENTITY_RESOLUTION_(400|404)/.test(error.message));
      if (!expectedMissingResource) console.error('[ENTITY-RESOLUTION] Public route could not be resolved:', error);
            // Core public routes remain available during a transient resolver failure.
      // This keeps portable static deployments independent of a precomputed route record.
      const normalizedPath = window.location.pathname.replace(/\/+$/, '') || '/';
      if (normalizedPath === '/blog' || normalizedPath.startsWith('/blog/')) {
        setResolvedPageContext(null);
        setApiError(null);
        setPage(Page.Blog);
        return;
      }
      setApiError('Cette page ne correspond à aucune ressource publique active.');
        }
    }, [commitResolvedPageContext, secretPath]);

    const navigateToEntity = useCallback(async (
        pageArchetype: PageArchetype,
        ids: CanonicalEntityIds = {},
        mode: 'push' | 'replace' | 'assign' = 'push'
    ) => {
        const context = await fetchCanonicalPageContext(pageArchetype, ids);
        if (mode === 'assign') {
            navigateResolvedPage(context, mode);
            return;
        }
        navigateResolvedPage(context, mode);
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, []);

    // Browser history changes only the URL. The server-issued ResolvedPageContext
    // is the sole authority that projects that URL back into application state.
    useEffect(() => {
        if (!isConfigLoaded) return;
        const onPopState = (event: PopStateEvent) => {
            void resolveBrowserLocation(event.state);
        };
        window.addEventListener('popstate', onPopState);
        if (serverResolvedPageContext && !/^#contact(?:\?|$)/iu.test(window.location.hash)) {
            commitResolvedPageContext(serverResolvedPageContext);
        } else {
            void resolveBrowserLocation(window.history.state);
        }
        return () => window.removeEventListener('popstate', onPopState);
    }, [isConfigLoaded, commitResolvedPageContext, resolveBrowserLocation]);

    // Entity state is selected only by stable IDs from ResolvedPageContext.
    // Mutable names and slugs are never used as client-side identities.
    useEffect(() => {
        const context = resolvedPageContext;
        if (!context) return;
        if (context.pageArchetype.startsWith('REPAIR_')) {
            const brand = context.brandId
                ? repairData.find(candidate => String(candidate.id) === context.brandId) || null
                : null;
            const model = brand && context.modelId
                ? brand.models.find(candidate => String(candidate.id) === context.modelId) || null
                : null;
            setSelectedBrand(brand);
            setSelectedModel(model);
            if (!model) {
                setSelectedRepairs([]);
            } else {
                const availableIds = new Set(model.repairs.map(repair => String(repair.id)));
                const relationshipId = context.modelServiceRelationshipId == null
                    ? null
                    : String(context.modelServiceRelationshipId);
                setSelectedRepairs(current => {
                    const retained = current.filter(repair => availableIds.has(String(repair.id)));
                    if (!relationshipId || retained.some(repair => String(repair.id) === relationshipId)) return retained;
                    const routeRepair = model.repairs.find(repair => String(repair.id) === relationshipId);
                    return routeRepair ? [...retained, routeRepair] : retained;
                });
            }
        } else if (context.pageArchetype !== 'APPOINTMENT_STATE' && context.pageArchetype !== 'CONTACT') {
            setSelectedBrand(null);
            setSelectedModel(null);
            setSelectedRepairs([]);
        }

        if (context.productId) {
            const product = parts.find(candidate => String(candidate.id) === context.productId) || null;
            if (context.pageArchetype === 'PRODUCT_PHONE') {
                setSelectedPhoneForPurchase(product);
                setSelectedPartForPurchase(null);
            } else {
                setSelectedPartForPurchase(product);
                setSelectedPhoneForPurchase(null);
            }
        } else if (!context.pageArchetype.startsWith('PRODUCT_')) {
            setSelectedPhoneForPurchase(null);
            setSelectedPartForPurchase(null);
        }

        if (context.blogPostId) {
            setSelectedBlogPost(blogPosts.find(post => String(post.id) === context.blogPostId) || null);
        } else if (context.pageArchetype !== 'BLOG_ARTICLE') {
            setSelectedBlogPost(null);
        }
    }, [resolvedPageContext, repairData, parts, blogPosts]);

    const notify = useCallback((message: string, type: AppNotification['type'] = 'info', submessage?: string, duration?: number) => {
        const id = Math.random().toString(36).substring(2, 9);
        setNotifications(prev => [...prev, { id, type, message, submessage, duration }]);
    }, []);

    const dismissNotification = useCallback((id: string) => {
        setNotifications(prev => prev.filter(n => n.id !== id));
    }, []);

    // Legacy Page callers are projected through the central entity authority.
    // Entity detail callers should use navigateToEntity with stable IDs.
    const handleSetPage: React.Dispatch<React.SetStateAction<Page>> = useCallback((value) => {
        const newPage = typeof value === 'function' ? value(page) : value;
        const rootArchetypes: Partial<Record<Page, PageArchetype>> = {
            [Page.Home]: 'HOME',
            [Page.Repairs]: 'REPAIR_ROOT',
            [Page.Accessories]: 'ACCESSORY_CATALOG_ROOT',
            [Page.BuyPhone]: 'PHONE_CATALOG_ROOT',
            [Page.Contact]: 'CONTACT',
            [Page.Blog]: 'BLOG_ROOT',
            [Page.SellPhone]: 'RACHAT',
            [Page.ImeiVerification]: 'STATIC',
            [Page.FindMyModel]: 'STATIC',
            [Page.Devices]: 'DEVICE_ROOT',
            [Page.Terms]: 'STATIC',
            [Page.Setup]: 'STATIC',
            [Page.Unsubscribe]: 'STATIC'
        };
        const staticSlugs: Partial<Record<Page, string>> = {
            [Page.ImeiVerification]: 'imei',
            [Page.FindMyModel]: 'aide',
            [Page.Terms]: 'terms',
            [Page.Setup]: 'setup',
            [Page.Unsubscribe]: 'unsubscribe'
        };

        if (newPage === Page.Admin || newPage === Page.LogoDesigner) {
            setPage(newPage);
        } else if (newPage === Page.ProductDetail && selectedPhoneForPurchase) {
            void navigateToEntity('PRODUCT_PHONE', { productId: selectedPhoneForPurchase.id });
        } else if (newPage === Page.AccessoryDetail && selectedPartForPurchase) {
            const archetype: PageArchetype = selectedPartForPurchase.catalogKind === 'ACCESSORY'
                ? 'PRODUCT_ACCESSORY'
                : selectedPartForPurchase.catalogKind === 'PHONE'
                    ? 'PRODUCT_PHONE'
                    : 'PRODUCT_OTHER';
            void navigateToEntity(archetype, { productId: selectedPartForPurchase.id });
        } else {
            const pageArchetype = rootArchetypes[newPage];
            if (pageArchetype) {
                const staticSlug = staticSlugs[newPage];
                void navigateToEntity(pageArchetype, staticSlug ? { staticSlug } : {});
            } else {
                setPage(newPage);
            }
        }
        if (newPage === Page.SellPhone) {
            setSellPhoneStep(1);
            setSellPhoneFormData({
                brand: '',
                model: '',
                capacity: '',
                condition_rating: 'Comme neuf',
                battery_health: '100',
                has_box: false,
                has_charger: false,
                has_invoice: false,
                issue_face_id: false,
                issue_camera: false,
                issue_icloud: false,
                customer_name: '',
                customer_email: '',
                customer_phone: '',
                customer_address: '',
                customer_iban: '',
                customer_twint: '',
                agreedToTerms: false,
                subscribedToNewsletter: false
            });
        }
    }, [navigateToEntity, page, selectedPhoneForPurchase, selectedPartForPurchase]);

    // Prevent Right Click and Dragging on Images
    useEffect(() => {
        const handleContextMenu = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (target && target.tagName && target.tagName.toLowerCase() === 'img') {
                e.preventDefault();
            }
        };

        const handleDragStart = (e: DragEvent) => {
            const target = e.target as HTMLElement;
            if (target && target.tagName && target.tagName.toLowerCase() === 'img') {
                e.preventDefault();
            }
        };

        document.addEventListener('contextmenu', handleContextMenu);
        document.addEventListener('dragstart', handleDragStart);

        return () => {
            document.removeEventListener('contextmenu', handleContextMenu);
            document.removeEventListener('dragstart', handleDragStart);
        };
    }, []);

    const safeJsonParse = useCallback(<T = unknown>(data: unknown, fallback: T): T => {
        if (!data) return fallback;
        if (typeof data !== 'string') {
            // Check if it's an object that should have been an array (legacy corruption)
            if (Array.isArray(fallback) && typeof data === 'object' && data !== null && !Array.isArray(data)) {
                return Object.values(data) as unknown as T;
            }
            return data as T;
        }
        try {
            const parsed = JSON.parse(data);
            // Handle corrupted array-to-object conversion from previous bug
            if (Array.isArray(fallback) && typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
                return Object.values(parsed) as unknown as T;
            }
            return parsed as T;
        } catch (e) {
            // Only log if it's not a standard non-json string (like a URL)
            console.error("[JSON PARSE ERROR]", e);
            if (data.toString().includes('{') || data.toString().includes('[')) {
                console.warn("[JSON PARSE ERROR] Failed to parse JSON, returning fallback.", { data: data.toString().substring(0, 100) });
            }
            return fallback;
        }
    }, []);

    // Dynamic config fetch from Backend (.env sync)
    const fetchRuntimeConfig = useCallback(async () => {
        let success = false;
        let attempt = 1;
        
        while (attempt <= 3 && !success) {
            try {
                setApiError(null);
                // Ensure request is clean
                const res = await axios.get('/api/public/config', { timeout: 5000 });
                if (res.data) {
                    if (res.data.adminPath) {
                        const activeAdminPath = res.data.adminPath;
                        setSecretPath(activeAdminPath);
                        try {
                            localStorage.setItem('mobilbo_admin_path', activeAdminPath);
                        } catch (e) {
                            console.error("[MOBILBO] Failed to save admin path to localStorage:", e);
                        }
                        const allAdminPaths = res.data.adminPaths ? res.data.adminPaths.map((p: string) => p.toLowerCase()) : [activeAdminPath.toLowerCase(), DEFAULT_ADMIN_PATH.toLowerCase()];
          allAdminPaths.push('/admin');
                        // Landing check for admin path
                        const currentLocPath = window.location.pathname.toLowerCase();

                        const isExactAdminPath = allAdminPaths.includes(currentLocPath);
                        if (isExactAdminPath) {
                            setPage(Page.Admin);
                        } else if (
                            currentLocPath === '/admin' ||
                            currentLocPath === '/admin/' ||
                            currentLocPath.startsWith('/admin/') ||
                            currentLocPath === '/administrator' ||
                            currentLocPath === '/dashboard' ||
                            currentLocPath === '/backoffice' ||
                            currentLocPath === '/login' ||
                            currentLocPath.startsWith('/admin-gestion-')
                        ) {
                            window.history.replaceState(null, '', '/');
                            setPage(Page.Home);
                        }
                    }
                    
                    if (res.data.footer) {
                        const info = res.data.footer;
                        setFooterInfo({
                            ...info, // Spread all backend fields
                            siteName: info.nom_du_magasin,
                            nom_du_magasin: info.nom_du_magasin,
                            email: info.email,
                            telephone: info.telephone,
                            adresse: info.adresse,
                            heures_ouverture: info.heures_ouverture,
                            iban: info.iban || '',
                            qr_iban: info.qr_iban || '',
                            smtp_host: info.smtp_host || '',
                            smtp_port: info.smtp_port || '',
                            smtp_user: info.smtp_user || '',
                            smtp_pass: info.smtp_pass || '',
                            smtp_encryption: info.smtp_encryption || 'STARTTLS',
                            imap_host: info.imap_host || '',
                            imap_port: info.imap_port || '',
                            imap_user: info.imap_user || '',
                            imap_pass: info.imap_pass || '',
                            imap_encryption: info.imap_encryption || 'SSL',
                            sms_api_key: info.sms_api_key || '',
                            meta_title: info.meta_title || '',
                            meta_description: info.meta_description || '',
                            meta_keywords: info.meta_keywords || '',
                            google_analytics_id: info.google_analytics_id || '',
                            facebook_pixel_id: info.facebook_pixel_id || '',
                            gtm_id: info.gtm_id || '',
                            google_site_verification: info.google_site_verification || '',
                            stripe_publishable_key: info.stripe_publishable_key || '',
                            stripe_secret_key: info.stripe_secret_key || '',
                            google_rating: info.google_rating || '4.9',
                            google_reviews_count: info.google_reviews_count || 142,
                            kanban_columns_config: info.kanban_columns_config || '',
                            ai_chatbot_enabled: res.data.settings?.ai_chatbot_enabled !== undefined 
                                ? (res.data.settings.ai_chatbot_enabled === 'true' || res.data.settings.ai_chatbot_enabled === true || res.data.settings.ai_chatbot_enabled === '1' || res.data.settings.ai_chatbot_enabled === 1) 
                                : (info.ai_chatbot_enabled !== false)
                        } as FooterInfoData);
                    }
                    
                    if (res.data.activeTheme) {
                        const t = res.data.activeTheme;
                        const lType = t.layoutType || t.layout_type || 'standard';
                        setActiveThemeConfig({
                            ...t,
                            layoutType: lType,
                            primaryColor: t.primaryColor || '#3b82f6',
                            isActive: true
                        });
                    } else {
                        setActiveThemeConfig({
                            id: 'default',
                            name: 'Crystal Premium',
                            fontFamily: 'Inter',
                            primaryColor: '#3b82f6',
                            layoutType: 'crystal',
                            isActive: true
                        });
                    }
                    
                    if (res.data.settings) {
                        const s = res.data.settings;
                        if (s.hero_slides) {
                            let slidesData = s.hero_slides;
                            if (typeof slidesData === 'string') {
                                slidesData = safeJsonParse<Record<string, unknown>[]>(slidesData, []);
                            }
                            
                            const parsedSlides = Array.isArray(slidesData) ? slidesData : [];
                            
                            // Data migration: map the old 'image' field to the new 'imageUrl' field
                            const migSlides = parsedSlides.map((slide: { imageUrl?: string; image?: string; [key: string]: unknown }) => ({
                                ...slide,
                                imageUrl: String(slide.imageUrl || slide.image || '')
                            })) as unknown as HeroSlide[];
                            setHeroSlides(migSlides);
                        }
                        if (s.showcase_products) {
                            try {
                                const parsed = typeof s.showcase_products === 'string' ? JSON.parse(s.showcase_products) : s.showcase_products;
                                if (Array.isArray(parsed)) {
                                    setShowcaseProducts(parsed.map(String));
                                }
                            } catch (e) {
                                console.error("Failed to parse showcase_products:", e);
                            }
                        }
                        if (s.promotion_settings) {
                            setPromotionSettings(safeJsonParse(s.promotion_settings, {
                                isEnabled: false,
                                title: '',
                                description: '',
                                image: '',
                                discountCode: '',
                                discountPercent: 0,
                                buttonLabel: 'Profiter',
                                targetPage: Page.Contact,
                                displayOnPages: [Page.Home],
                                startDate: '',
                                endDate: '',
                                showOncePerSession: true,
                                displayDelay: 2,
                                overlayBlur: true,
                                isDismissible: true
                            }));
                        }
                    }
                    success = true;
                }
            } catch (e: unknown) {
                if (axios.isAxiosError(e)) {
                    console.warn(`[MOBILBO] Config load failed - status: ${e.response?.status || 'Network Error'} (Attempt ${attempt}/3)`);
                } else {
                    console.warn(`[MOBILBO] Config load failed (Attempt ${attempt}/3):`, e);
                }
                
                if (attempt === 3) {
                    setApiError("Le serveur API n'est pas accessible. Certains services peuvent être temporairement indisponibles.");
                    
                    // Use hard-coded fallback if already on admin path
                    if (window.location.pathname === DEFAULT_ADMIN_PATH) {
                        setPage(Page.Admin);
                    }
                } else {
                    // Wait 2 seconds before retrying
                    await new Promise(resolve => setTimeout(resolve, 2000));
                }
            }
            attempt++;
        }
        setIsConfigLoaded(true);
    }, [safeJsonParse]);

    useEffect(() => {
        fetchRuntimeConfig();
    }, [fetchRuntimeConfig]);

    // Publish the navigation index independently of the larger service catalog.
    useEffect(() => {
        const controller = new AbortController();
        const publishCatalog = (brands: CatalogRow[], models: CatalogRow[], services: CatalogRow[]) => {
                    catalogIndexPublished();
                    console.log("[DATA] Raw counts - Brands:", brands.length, "Models:", models.length, "Services:", services.length);
                    if (models.length > 0) {
                        console.log("[DATA] Sample model brand_id:", models[0].brand_id, "type:", typeof models[0].brand_id);
                    }

                    // Pre-process for O(1) lookups instead of nested filters
                    const modelsByBrandId = new Map<string, Record<string, unknown>[]>();
                    const modelsByBrandName = new Map<string, Record<string, unknown>[]>();
                    
                    models.forEach((m: Record<string, unknown>) => {
                        const bid = String(m.brand_id || '').trim().toLowerCase();
                        if (bid) {
                            if (!modelsByBrandId.has(bid)) modelsByBrandId.set(bid, []);
                            modelsByBrandId.get(bid)?.push(m);
                        }
                        const bn = String(m.brandName || '').trim().toLowerCase();
                        if (bn) {
                            if (!modelsByBrandName.has(bn)) modelsByBrandName.set(bn, []);
                            modelsByBrandName.get(bn)?.push(m);
                        }
                    });

                    const servicesByModelId = new Map<string, Record<string, unknown>[]>();
                    const servicesByBrandPlusModel = new Map<string, Record<string, unknown>[]>();
                    
                    services.forEach((s: Record<string, unknown>) => {
                        const mid = String(s.model_id || '').trim().toLowerCase();
                        if (mid) {
                            if (!servicesByModelId.has(mid)) servicesByModelId.set(mid, []);
                            servicesByModelId.get(mid)?.push(s);
                        }
                        const bName = String(s.brand || '').trim().toLowerCase();
                        const mName = String(s.model || '').trim().toLowerCase();
                        if (bName && mName) {
                            const key = `${bName}|${mName}`;
                            if (!servicesByBrandPlusModel.has(key)) servicesByBrandPlusModel.set(key, []);
                            servicesByBrandPlusModel.get(key)?.push(s);
                        }
                    });

                    const reconstructed: Brand[] = brands.map((b: Record<string, unknown>) => {
                        const brandIdStr = String(b.id || '').trim().toLowerCase();
                        const brandNameStr = String(b.name || '').trim().toLowerCase();
                        
                        // Combine models from both lookup strategies
                        const modelsById = modelsByBrandId.get(brandIdStr) || [];
                        const modelsByName = modelsByBrandName.get(brandNameStr) || [];
                        
                        // Special cases from original logic
                        let specialModels: Record<string, unknown>[] = [];
                        if (brandIdStr === 'watch' || brandIdStr === 'apple') {
                            specialModels = models.filter((m: Record<string, unknown>) => {
                                const mName = String(m.name || '').toLowerCase();
                                return (brandIdStr === 'watch' && mName.includes('watch')) ||
                                       (brandIdStr === 'apple' && mName.includes('iphone'));
                            });
                        }

                        // Unique set of models for this brand
                        const uniqueModels = Array.from(new Map([...modelsById, ...modelsByName, ...specialModels].map(m => [String(m.id), m])).values());

                        const brandModels = uniqueModels.map((m: Record<string, unknown>) => {
                            const modelIdStr = String(m.id || '').trim().toLowerCase();
                            const mNameStr = String(m.name || '').trim();
                            const bPlusMKey = `${brandNameStr}|${mNameStr.toLowerCase()}`;
                            
                            const repairsFromId = servicesByModelId.get(modelIdStr) || [];
                            const repairsByName = servicesByBrandPlusModel.get(bPlusMKey) || [];
                            
                            const uniqueRepairs = Array.from(new Map([...repairsFromId, ...repairsByName].map(r => [String(r.id), r])).values());

                            const repairList = uniqueRepairs.length > 0 ? uniqueRepairs.map(projectPublicRepair) : safeJsonParse(m.repairs, []);

                            return {
                                id: String(m.id || `m-${Math.random()}`),
                                masterStableId: String(m.master_stable_id || ''),
                                name: String(m.name || 'Unknown'),
                                image: String(m.image_url || m.image || ''),
                                series: String(m.series || 'Autres'),
                                repairs: repairList,
                                repairsLoaded: m.repairsLoaded !== false,
                                images360: safeJsonParse(m.images360 as string, []),
                                focusKeyword: String(m.focus_keyword || ''),
                                seoTitle: String(m.seo_title || m.meta_title || ''),
                                 seoDescription: String(m.seo_description || m.meta_description || ''),
                                 slug: String(m.slug || ''),
                                 canonicalPath: String(m.canonical_path || ''),
                                altText: String(m.alt_text || m.image_alt || ''),
                                image_title: String(m.image_title || ''),
                                image_alt: String(m.image_alt || ''),
                                image_keywords: String(m.image_keywords || ''),
                                image_description: String(m.image_description || ''),
                                image_services: String(m.image_services || ''),
                                salePrice: Number(m.salePrice) || 0,
                                stock: Number(m.stock) || 0,
                                condition: String(m.condition_text || m.condition || ''),
                                batteryHealth: String(m.batteryHealth || ''),
                                warranty: String(m.warranty || ''),
                                storage: String(m.storage || ''),
                                camera: String(m.camera || ''),
                                description: String(m.description || ''),
                                highlights: safeJsonParse(m.highlights as string, []),
                                isActive: m.is_active !== undefined ? m.is_active === 1 || m.is_active === true : true,
                                specs: safeJsonParse(m.specs as string, {}),
                                options: safeJsonParse(m.options as string, null) || safeJsonParse(m.specs as string, null),
                                videoUrl: (() => {
                                    const v = String(m.video_url || m.videoUrl || '').trim();
                                    return (v === 'null' || v === 'undefined') ? '' : v;
                                })()
                            };
                        });

                        // Fix logo path if it's just a filename
                        let logoPath = String(b.logo || '');
                        if (logoPath && !logoPath.startsWith('http') && !logoPath.startsWith('/')) {
                            logoPath = `/assets/brands/${logoPath}`;
                        }

                        return {
                            id: String(b.id || ''),
                             name: String(b.name || ''),
                             slug: String(b.slug || ''),
                             canonicalPath: String(b.canonical_path || ''),
                            logo: logoPath,
                            models: brandModels,
                            description: String(b.description || ''),
                            focusKeyword: String(b.focus_keyword || ''),
                            seoTitle: String(b.seo_title || b.meta_title || ''),
                            seoDescription: String(b.seo_description || b.meta_description || ''),
                            isActive: b.is_active !== undefined ? b.is_active === 1 || b.is_active === true : true
                        };
                    });

                    setRepairData(current => {
                        const loaded = new Map(current.flatMap(brand => brand.models).filter(model => model.repairsLoaded === true).map(model => [model.id, model]));
                        return reconstructed.map(brand => ({ ...brand, models: brand.models.map(model => {
                            const detail = loaded.get(model.id);
                            return detail ? { ...model, repairs: detail.repairs, repairsLoaded: true } : model;
                        }) }));
                    });
                    console.log("[DATA] Reconstructed repair data:", reconstructed.length, "brands");
                    console.log("[DATA] Total models across all brands:", reconstructed.reduce((acc, b) => acc + b.models.length, 0));
                    if (reconstructed.length > 0) {
                        console.log("[DATA] Sample brand (first):", reconstructed[0].name, "with", reconstructed[0].models.length, "models");
                    }
        };
        void loadProgressiveRepairCatalog(
            path => axios.get<unknown>(path, { signal: controller.signal, transformResponse: [catalogResponseTransform(path)] }).then(response => response.data),
            publishCatalog,
            controller.signal,
        ).catch(error => {
            if (!controller.signal.aborted) console.error('[DATA] Failed to fetch repairs:', error);
        });
        return () => controller.abort();
    }, [safeJsonParse]);

    useEffect(() => {
        catalogRenderCommitted(repairData.length, repairData.reduce((total, brand) => total + brand.models.length, 0));
    }, [repairData]);

    // Fetch only the selected model. Cancellation prevents an old selection from
    // replacing another entity, and SSR identity remains available during the read.
    const selectedModelId = canonicalAppointmentIdentity(selectedModel?.id);
    useEffect(() => {
        if (!selectedModelId || selectedModelId === 'external') return;
        const controller = new AbortController();
        void loadSelectedModelRepairs(
            path => axios.get<unknown>(path, { signal: controller.signal, transformResponse: [catalogResponseTransform(path)] }).then(response => response.data),
            selectedModelId,
        ).then(repairs => {
            if (controller.signal.aborted) return;
            setRepairData(current => current.map(brand => ({ ...brand, models: brand.models.map(model =>
                canonicalAppointmentIdentity(model.id) === selectedModelId ? { ...model, repairs, repairsLoaded: true } : model) })));
            setSelectedModel(current => canonicalAppointmentIdentity(current?.id) === selectedModelId ? { ...current, repairs, repairsLoaded: true } : current);
        }).catch(error => {
            if (!controller.signal.aborted) console.error('[DATA] Selected model services failed:', error);
        });
        return () => controller.abort();
    }, [selectedModelId]);

    // Fetch Products from Backend
    useEffect(() => {
        const productPageActive = [Page.BuyPhone, Page.Accessories, Page.ProductDetail, Page.AccessoryDetail].includes(page);
        if (!productPageActive && !shouldLoadAuxiliaryUi) return;
        const fetchProducts = async () => {
            try {
                const res = await axios.get('/api/public/products');
                if (res.data && Array.isArray(res.data)) {
                    // Use database products exclusively
                    const dbProducts: Part[] = res.data.map((p: BackendProduct): Part => {
                        const parsedGallery = safeJsonParse<(string | GalleryImage)[]>(p.gallery, []);
                        const firstGalleryImg = parsedGallery && parsedGallery.length > 0
                            ? (typeof parsedGallery[0] === 'string' ? parsedGallery[0] : parsedGallery[0]?.url)
                            : '';
                        return {
                            id: p.id,
                            name: p.name,
                            mainCategory: p.category || p.mainCategory || 'Accessoires',
                            productType: p.product_type || p.productType || 'product',
                            catalogKind: p.catalogKind || 'OTHER',
                            catalogProjectionAuthority: p.catalogProjectionAuthority,
                            categoryId: p.category_id || p.categoryId || null,
                             price: Number(p.price || 0),
                             image: p.image_url || p.image || firstGalleryImg || '',
                             videoUrl: p.video_url || p.videoUrl || '',
                             currencyCode: p.currencyCode,
                             slug: p.slug,
                             canonicalPath: p.canonicalPath,
                             modelId: p.modelId ?? p.model_id ?? null,
                             model_id: p.model_id ?? p.modelId ?? null,
                             deviceKnowledgePath: p.deviceKnowledgePath ?? null,
                             repairModelPath: p.repairModelPath ?? null,
                             gallery: parsedGallery,
                             highlights: safeJsonParse<string[]>(p.highlights, []),
                             altText: p.alt_text || p.image_alt,
                            seoTitle: p.seoTitle || p.seo_title || p.meta_title,
                            seoDescription: p.seoDescription || p.seo_description || p.meta_description,
                            sku: p.sku || '',
                             stock: Number(p.stock || 0),
                             brand: p.brand || '',
                             description: p.description || '',
                            imei: p.imei,
                            serialNumber: p.serialNumber,
                             ...mapPublicProductCharacteristics(p)
                         };
                    });
                    
                    setParts(dbProducts);
                }
            } catch (e) {
        console.error("Failed to fetch products:", e);
        setParts(PARTS_DATA);
      } finally {
        setIsProductCatalogLoaded(true);
      }
    };
        fetchProducts();
    }, [page, safeJsonParse, shouldLoadAuxiliaryUi]);

    // Fetch Blog Posts from the API, with a deployment-local fallback.
  useEffect(() => {
    if (page !== Page.Blog && !shouldLoadAuxiliaryUi) return;
    const fetchBlogs = async () => {
      try {
        const res = await axios.get('/api/public/blog-posts');
        if (Array.isArray(res.data) && res.data.length > 0) {
          setBlogPosts(res.data);
          return;
        }
      } catch (e) {
        console.warn('API blog indisponible, utilisation de l’index public.', e);
      }
      try {
        const fallback = await axios.get('/blog/posts.json', { params: { v: '1' } });
        if (Array.isArray(fallback.data)) setBlogPosts(fallback.data);
      } catch (e) {
        console.error('Failed to fetch the public blog index:', e);
      }
    };
    fetchBlogs();
  }, [page, shouldLoadAuxiliaryUi]);

  // Public route interpretation is centralized in resolveBrowserLocation.
    // Components may request navigation only through navigateToEntity.
    // Persist admin state interceptors
    useEffect(() => {
        let isRefreshing = false;
        type RefreshQueueEntry = {
      resolve: (token: string | null) => void;
      reject: (reason?: unknown) => void;
    };
    let failedQueue: RefreshQueueEntry[] = [];

    const processQueue = (error: unknown, token: string | null = null) => {
      failedQueue.forEach(prom => {
        if (error) {
          prom.reject(error);
        } else {
          prom.resolve(token);
        }
      });
      failedQueue = [];
    };

    const interceptor = axios.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config;

                // If error is 401 and we haven't already retried this request
                const requestUrl = String(originalRequest?.url || ''); const isAuthoritativeSessionRequest = requestUrl.includes('/api/admin/summary') || requestUrl.includes('/api/admin/session') || requestUrl.includes('/api/admin/me'); if (error.response && error.response.status === 401 && !originalRequest?._retry && isAuthoritativeSessionRequest) {
                    // Do not attempt refresh on refresh calls themselves to avoid loops
                    if (originalRequest.url && originalRequest.url.includes('/api/admin/refresh')) {
                        return Promise.reject(error);
                    }

                    const currentStoredToken = localStorage.getItem('mobilbo_token');
                    const authHeaderVal = originalRequest.headers['Authorization'];
                    const sentToken = (typeof authHeaderVal === 'string' ? authHeaderVal.replace('Bearer ', '') : '') || originalRequest.headers['X-Auth-Token'];

                    if (currentStoredToken && currentStoredToken !== sentToken) {
                        // A fresh token is already available! Retrying immediately without triggering refresh
                        originalRequest._retry = true;
                        originalRequest.headers['Authorization'] = 'Bearer ' + currentStoredToken;
                        originalRequest.headers['X-Auth-Token'] = currentStoredToken;
                        return axios(originalRequest);
                    }

                    const storedRefreshToken = localStorage.getItem('mobilbo_refresh_token');
                    
                    // If we have a refresh token, try to refresh
                    if (storedRefreshToken && storedRefreshToken !== 'null' && storedRefreshToken !== 'undefined') {
                        if (isRefreshing) {
                            return new Promise((resolve, reject) => {
                                failedQueue.push({ resolve, reject });
                            }).then(token => {
                                originalRequest.headers['Authorization'] = 'Bearer ' + token;
                                originalRequest.headers['X-Auth-Token'] = token;
                                return axios(originalRequest);
                            }).catch(err => {
                                return Promise.reject(err);
                            });
                        }

                        originalRequest._retry = true;
                        isRefreshing = true;

                        try {
                            const res = await axios.post('/api/admin/refresh', { refreshToken: storedRefreshToken });
                            if (res.data && res.data.token) {
                                const newToken = res.data.token;
                                const newRefreshToken = res.data.refreshToken || storedRefreshToken;
                                
                                localStorage.setItem('mobilbo_token', newToken);
                                localStorage.setItem('mobilbo_refresh_token', newRefreshToken);

                                axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
                                axios.defaults.headers.common['X-Auth-Token'] = newToken;
                                originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                                originalRequest.headers['X-Auth-Token'] = newToken;

                                processQueue(null, newToken);
                                isRefreshing = false;
                                return axios(originalRequest);
                            }
                        } catch (refreshError) {
                            processQueue(refreshError, null);
                            isRefreshing = false;
                            
                            // Silent refresh failed, perform logout
                            localStorage.removeItem('mobilbo_token');
                            localStorage.removeItem('mobilbo_refresh_token');
                            delete axios.defaults.headers.common['Authorization'];
                            delete axios.defaults.headers.common['X-Auth-Token'];
                            setIsAdmin(false);
                            setPage(Page.Admin);
                            return Promise.reject(refreshError);
                        }
                    }

                    // No refresh token available, logout
                    localStorage.removeItem('mobilbo_token');
                    localStorage.removeItem('mobilbo_refresh_token');
                    delete axios.defaults.headers.common['Authorization'];
                    delete axios.defaults.headers.common['X-Auth-Token'];
                    setIsAdmin(false);
                    setPage(Page.Admin);
                }
                return Promise.reject(error);
            }
        );

        return () => {
            axios.interceptors.response.eject(interceptor);
        };
    }, []);

    useEffect(() => {
        if (isAdmin) {
            axios.get('/api/admin/themes')
                .then(res => {
                    if (res.data) {
                        setSavedThemes(res.data.map((t: Record<string, unknown>) => ({
                            id: String(t.id),
                            name: t.name,
                            htmlStructure: t.htmlStructure,
                            customCss: t.customCss,
                            fontFamily: t.fontFamily || 'Inter',
                            layoutType: t.layoutType || 'standard',
                            isActive: t.is_active === 1
                        })));
                    }
                })
                .catch(err => {
                    const isAuthError = err.response?.status === 401 || err.response?.status === 403 || err.message?.includes('401') || err.message?.includes('403');
                    if (!isAuthError) {
                        console.error("Failed to fetch custom themes:", err);
                    } else {
                        console.warn("Unauthorized custom theme fetch - token might be expired or invalid.");
                    }
                });
        }
    }, [isAdmin]);

    // URL projection is owned by Canonical Projection; no page-state effect may synthesize paths.
    const login = async (u: string, p: string) => {
        try {
            const response = await axios.post('/api/admin/login', { username: u, password: p });
            const data = response.data;
            
            if (data.token) {
                localStorage.setItem('mobilbo_token', data.token);
                axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
                axios.defaults.headers.common['X-Auth-Token'] = data.token;
            }
            if (data.refreshToken) {
                localStorage.setItem('mobilbo_refresh_token', data.refreshToken);
            }
            setIsAdmin(true);
            return true;
        } catch (error: unknown) {
      console.error("Login Error:", error);
      if (axios.isAxiosError(error) && error.response) {
        console.error("Login Error Response Data:", error.response.data);
        console.error("Login Error Response Status:", error.response.status);
      }
      return false;
    }
    };
    
    const logout = useCallback(() => { 
        setIsAdmin(false); 
        handleSetPage(Page.Home); 
        localStorage.removeItem('mobilbo_token');
        localStorage.removeItem('mobilbo_refresh_token');
        delete axios.defaults.headers.common['Authorization'];
        delete axios.defaults.headers.common['X-Auth-Token'];
        axios.post('/api/admin/logout', {}).catch(() => {});
    }, [handleSetPage]);

    const addToCart = useCallback((item: PhoneModel | Part | UnifiedCartItem, quantity: number, category: UnifiedCartItem['category']) => {
        if (!item) return;
        const itemAsRecord = item as unknown as Record<string, unknown>;
        const selectedServiceOptionId = typeof itemAsRecord.selectedServiceOptionId === 'string'
            ? itemAsRecord.selectedServiceOptionId
            : null;

        // Find the product's actual stock from the live parts array
        const actualProduct = parts.find(p => String(p.id) === String(item.id));
        const maxStock = actualProduct ? (actualProduct.stock !== undefined ? actualProduct.stock : 999) : 999;

        setCart(prev => {
            const existing = prev.find(i => String(i.id) === String(item.id)
                && i.category === category
                && (i.selectedServiceOptionId || null) === selectedServiceOptionId);
            if (existing) {
                const newQty = existing.quantity + quantity;
                if (newQty > maxStock) {
                    notify(`Désolé, seulement ${maxStock} unité(s) de "${item.name}" sont disponibles en stock.`, "error");
                    return prev.map(i => (String(i.id) === String(item.id) && i.category === category && (i.selectedServiceOptionId || null) === selectedServiceOptionId) ? { ...i, quantity: maxStock } : i);
                }
                return prev.map(i => (String(i.id) === String(item.id) && i.category === category && (i.selectedServiceOptionId || null) === selectedServiceOptionId) ? { ...i, quantity: newQty } : i);
            }
            
            let finalQty = quantity;
            if (finalQty > maxStock) {
                notify(`Désolé, seulement ${maxStock} unité(s) de "${item.name}" sont disponibles en stock.`, "error");
                finalQty = maxStock;
            }
            if (finalQty <= 0) return prev;
            
            const parsePrice = (val: string | number | null | undefined): number => {
                if (typeof val === 'number') return isNaN(val) ? 0 : val;
                if (!val) return 0;
                const cleaned = val.toString().replace(/[^\d.,]/g, '').replace(',', '.');
                const parsed = parseFloat(cleaned);
                return isNaN(parsed) ? 0 : parsed;
            };

            // Handle different price properties (PhoneModel uses salePrice, others use price)
            let finalPrice = 0;
            if (itemAsRecord.salePrice !== undefined) {
                finalPrice = parsePrice(itemAsRecord.salePrice as string | number | null | undefined);
            } else if (itemAsRecord.price !== undefined) {
                finalPrice = parsePrice(itemAsRecord.price as string | number | null | undefined);
            }
            
            const specs = (itemAsRecord.specs as Record<string, string | undefined>) || {};
            
            return [...prev, { 
                id: item.id, 
                name: item.name, 
                price: finalPrice || 0, 
                quantity: Number(finalQty) || 1, 
                image: item.image || (itemAsRecord.image_url as string | undefined), 
                category, 
                productType: (itemAsRecord.productType as string) || 'Appareil',
                color: (itemAsRecord.color as string) || specs.color,
                storage: (itemAsRecord.storage as string) || specs.storage,
                capacity: (itemAsRecord.capacity as string) || specs.battery,
                imei: itemAsRecord.imei as string,
                serialNumber: itemAsRecord.serialNumber as string,
                selectedServiceOptionId,
                selectedServiceOptionLabel: typeof itemAsRecord.selectedServiceOptionLabel === 'string' ? itemAsRecord.selectedServiceOptionLabel : null,
                serviceOptionSnapshot: (itemAsRecord.serviceOptionSnapshot as ServiceOfferOption | null | undefined) || null
            }];
        });
    }, [parts, notify]);

    const updateCartQuantity = useCallback((id: string | number, category: string, qty: number) => setCart(prev => prev.map(i => {
        if (String(i.id) === String(id) && i.category === category) {
            const actualProduct = parts.find(p => String(p.id) === String(id));
            const maxStock = actualProduct ? (actualProduct.stock !== undefined ? actualProduct.stock : 999) : 999;
            if (qty > maxStock) {
                notify(`Désolé, seulement ${maxStock} unité(s) sont disponibles en stock pour ce produit.`, "error");
                return { ...i, quantity: maxStock };
            }
            return { ...i, quantity: Math.max(0, qty) };
        }
        return i;
    }).filter(i => i.quantity > 0)), [parts, notify]);
    const removeFromCart = useCallback((id: string | number, category: string) => setCart(prev => prev.filter(i => !(i.id === id && i.category === category))), []);
    
    const addOrder = useCallback(async (o: Order) => {
        try {
            const requestId = `web-order:${String(o.id).replace(/[^A-Za-z0-9._:-]/g, '-') }`;
            await axios.post('/api/public/orders', {
                idempotencyKey: requestId,
                lines: o.items.map((item) => ({
                    kind: item.category === 'Repair' ? 'REPAIR_SERVICE' : 'PRODUCT',
                    ...(item.category === 'Repair'
                        ? { repairServiceId: item.id, serviceOptionId: item.selectedServiceOptionId }
                        : { productId: item.id }),
                    quantity: item.quantity
                })),
                customer: {
                    name: o.clientName,
                    phone: o.clientPhone,
                    email: o.clientEmail,
                    address: o.clientAddress
                },
                paymentMethod: o.paymentMethod,
                paymentReference: o.paymentReference,
                checkoutToken: o.checkoutToken,
                shippingOptionId: o.shippingOptionId,
                discountCode: o.discountCode,
                notes: o.notes
            }, { headers: { 'Idempotency-Key': requestId } });
            setOrders(prev => [o, ...prev]);
        } catch (error) {
            console.error("Failed to save order:", error);
            throw error;
        }
    }, []);

    const addAppointment = useCallback(async (a: Appointment) => {
        try {
            const aptData = {
                bookingRequestId: a.bookingRequestId,
                documentReference: a.documentReference,
                clientName: a.clientName,
                clientEmail: a.clientEmail,
                clientPhone: a.clientPhone,
                model: a.model,
                repairs: a.repairs,
                dateTime: a.dateTime,
                notes: a.notes,
                totalPrice: a.totalPrice,
                pdfBase64: a.pdfBase64,
                deliveryMode: a.deliveryMode,
                postalAddress: a.postalAddress,
                postalZip: a.postalZip,
                postalCity: a.postalCity,
                appointmentToken: a.publicAccessToken
            };
            
            const isKnownId = a.id && !isNaN(Number(a.id)) && Number(a.id) < 1000000000;
            
            const res = isKnownId 
                ? await axios.put(`/api/public/appointments/${a.id}`, aptData)
                : await axios.post('/api/public/appointments', aptData);
                
            const newApt = { ...a, id: res.data.id || a.id, documentReference: res.data.documentReference || a.documentReference, publicAccessToken: res.data.appointmentToken || a.publicAccessToken };
            setAppointments(prev => {
                if (isKnownId) {
                    return prev.map(item => item.id === a.id ? newApt : item);
                }
                return [newApt, ...prev];
            });
            return newApt;
        } catch (err) {
            console.error("Failed to add/update appointment:", err);
            throw err;
        }
    }, []);

    const addQuoteRequest = useCallback(async (q: QuoteRequest) => {
        try {
            await axios.post('/api/public/quotes', q);
            setQuoteRequests(prev => [q, ...prev]);
        } catch (e) {
            console.error("Failed to send quote request:", e);
            setQuoteRequests(prev => [q, ...prev]);
        }
    }, []);

    const addBuybackRequest = useCallback(async (b: BuybackRequest) => {
        try {
            const response = await axios.post('/api/buyback/submit', b);
            const persistedRequest = { ...b, id: response.data.id };
            setBuybackRequests((current) => [
                persistedRequest,
                ...current.filter((request) => request.id !== response.data.id)
            ]);
            return response.data;
        } catch (error) {
            console.error("Failed to send buyback request:", error);
            throw error;
        }
    }, []);

    const importNewTheme = useCallback(async (t: CustomThemeConfig) => { 
        try {
            await axios.post('/api/admin/themes', t);
            if (t.isActive) {
                await axios.post(`/api/admin/themes/${t.id}/activate`);
            }
            setSavedThemes(prev => [t, ...prev.filter(x => x.id !== t.id).map(x => ({...x, isActive: t.isActive ? false : x.isActive}))]); 
            if (t.isActive) {
                setActiveThemeConfig(t); 
            }
            return true;
        } catch (e) {
            console.error("Failed to save theme:", e);
            throw e;
        }
    }, []);

    const fetchThemes = useCallback(async () => {
        try {
            const res = await axios.get('/api/admin/themes');
            if (Array.isArray(res.data)) {
                const normalizedThemes = res.data.map(t => ({
                    ...t,
                    isActive: t.isActive === true || t.is_active === 1 || t.is_active === true
                }));
                setSavedThemes(normalizedThemes);
                const active = normalizedThemes.find(t => t.isActive);
                if (active) setActiveThemeConfig(active);
            }
        } catch (error: unknown) {
        const status = axios.isAxiosError(error) ? error.response?.status : undefined;
        const message = error instanceof Error ? error.message : '';
        const isAuthError = status === 401 || status === 403 || message.includes('401') || message.includes('403');
        if (!isAuthError) {
          console.error("Failed to fetch themes:", error);
        } else {
          console.warn("Unauthorized themes fetch - token might be expired or invalid.");
        }
      }
    }, []);

    useEffect(() => {
        if (isAdmin) {
            fetchThemes();
        }
    }, [isAdmin, fetchThemes]);

    const updateTheme = useCallback(async (id: string, updates: Partial<CustomThemeConfig>) => {
        try {
            const currentTheme = savedThemes.find(t => t.id === id) || (activeThemeConfig?.id === id ? activeThemeConfig : null);
            if (!currentTheme) {
                console.warn("[THEME] Attempted to update non-existent theme:", id);
                return;
            }
            const updated = { ...currentTheme, ...updates };
            await axios.post('/api/admin/themes', updated);
            
            setSavedThemes(prev => {
                const exists = prev.some(t => t.id === id);
                if (exists) return prev.map(t => t.id === id ? updated : t);
                return [...prev, updated];
            });
            
            if (activeThemeConfig?.id === id) {
                setActiveThemeConfig(updated);
            }
        } catch (e) {
            console.error("Failed to update theme:", e);
        }
    }, [savedThemes, activeThemeConfig]);

    // Dynamic Style Injection
    useEffect(() => {
        const root = document.documentElement;
        
        // Convert hex to rgb for shadow variables
        const hexToRgb = (hex: string) => {
            const r = parseInt(hex.slice(1, 3), 16);
            const g = parseInt(hex.slice(3, 5), 16);
            const b = parseInt(hex.slice(5, 7), 16);
            return `${r}, ${g}, ${b}`;
        };

        if (page === Page.Admin) {
            // Absolute Backoffice Immunity: Reset standard styling tokens in admin dashboard
            root.style.setProperty('--primary', '#2563eb');
            root.style.setProperty('--primary-rgb', '37, 99, 235');
            root.style.setProperty('--font-main', STABLE_SYSTEM_FONT_STACK);
            document.body.style.fontFamily = STABLE_SYSTEM_FONT_STACK;
            
            const styleId = 'custom-theme-styles';
            const styleElement = document.getElementById(styleId);
            if (styleElement) {
                styleElement.innerHTML = '';
            }

            // Clean up custom theme scripts or styles
            const jsId = 'custom-theme-js';
            const existingJs = document.getElementById(jsId);
            if (existingJs) {
                existingJs.remove();
            }

            // Remove loaded theme tokens to preserve pristine Admin UI state
            try {
                if (activeThemeConfig?.tokens_json) {
                    const tokens = JSON.parse(activeThemeConfig.tokens_json);
                    Object.keys(tokens).forEach(key => {
                        const cssVarName = key.startsWith('--') ? key : `--theme-${key}`;
                        root.style.removeProperty(cssVarName);
                    });
                }
            } catch {
                // Ignore parsing errors for missing json
            }

            return;
        }

        if (activeThemeConfig) {
            const color = activeThemeConfig.primaryColor || '#3b82f6';
            const font = activeThemeConfig.fontFamily || 'Inter';

            // The first paint always uses the stable operating-system metrics.
            // Governed display faces are optional decoration: `display=optional`
            // prevents a late font swap from creating CLS on this or future Themes.
            const fontQuery = GOVERNED_THEME_FONTS[font];
            if (fontQuery && !document.querySelector(`link[data-theme-font="${font}"]`)) {
                const fontLink = document.createElement('link');
                fontLink.rel = 'stylesheet';
                fontLink.media = 'print';
                fontLink.dataset.themeFont = font;
                fontLink.href = `https://fonts.googleapis.com/css2?family=${fontQuery}&display=optional`;
                fontLink.onload = () => { fontLink.media = 'all'; };
                document.head.appendChild(fontLink);
            }
            
            root.style.setProperty('--primary', color);
            root.style.setProperty('--primary-rgb', hexToRgb(color));
            
            // Protect readability: If it's a specialty display/technical font, restrict body to system/Inter, while headings are styled
            const specialtyFonts = ['JetBrains Mono', 'Space Grotesk', 'Playfair Display', 'Outfit'];
            const isSpecialty = specialtyFonts.includes(font);
            
            const stableBodyFont = isSpecialty || font === 'Inter' ? STABLE_SYSTEM_FONT_STACK : font;
            root.style.setProperty('--font-main', stableBodyFont);
            document.body.style.fontFamily = stableBodyFont;
            
            // Handle custom CSS injection
            const styleId = 'custom-theme-styles';
            let styleElement = document.getElementById(styleId);
            
            // Define rigorous style safety overrides
            let legibilityOverrides = '';
            if (isSpecialty) {
                legibilityOverrides = `
                  /* Rigorous Typography Safeguards */
                  body, p, li, td, input, select, textarea, label, .readable-text {
                    font-family: ${STABLE_SYSTEM_FONT_STACK} !important;
                    letter-spacing: normal !important;
                  }
                  h1, h2, h3, h4, h5, h6, .theme-heading, [class*="font-serif"] {
                    font-family: '${font}', ${STABLE_SYSTEM_FONT_STACK} !important;
                  }
                  button, .btn, .cta-button, [class*="tracking-[0."] {
                    font-family: '${font}', ${STABLE_SYSTEM_FONT_STACK} !important;
                  }
                `;
            }
            
            const finalCss = activeThemeConfig.customCss 
                ? `${legibilityOverrides}\n${activeThemeConfig.customCss}`
                : legibilityOverrides;
                
            if (finalCss) {
                if (!styleElement) {
                    styleElement = document.createElement('style');
                    styleElement.id = styleId;
                    document.head.appendChild(styleElement);
                }
                styleElement.innerHTML = finalCss;
            } else if (styleElement) {
                styleElement.innerHTML = '';
            }

            // Inject Custom Design Tokens as CSS Variables
            if (activeThemeConfig.tokens_json) {
                try {
                    const tokens = JSON.parse(activeThemeConfig.tokens_json);
                    Object.keys(tokens).forEach(key => {
                        const cssVarName = key.startsWith('--') ? key : `--theme-${key}`;
                        root.style.setProperty(cssVarName, tokens[key]);
                    });
                } catch (e) {
                    console.warn("[THEME-TOKENS]: Invalid tokens JSON format:", e);
                }
            }

            // Clean up and Inject Custom Interactive JS/Motion blocks
            const jsId = 'custom-theme-js';
            const existingJs = document.getElementById(jsId);
            if (existingJs) {
                existingJs.remove();
            }

            if (activeThemeConfig.js_code) {
                try {
                    const script = document.createElement('script');
                    script.id = jsId;
                    script.type = 'text/javascript';
                    script.innerHTML = `
                        (function() {
                            try {
                                ${activeThemeConfig.js_code}
                            } catch (err) {
                                console.error("[THEME-JS-ERROR]:", err);
                            }
                        })();
                    `;
                    document.body.appendChild(script);
                } catch (jsErr) {
                    console.error("[THEME-JS]: Script injection failed:", jsErr);
                }
            }
        }
    }, [activeThemeConfig, page, isAdmin]);

    const deleteTheme = useCallback(async (id: string) => {
        try {
            await axios.delete(`/api/admin/themes/${id}`);
            setSavedThemes(prev => prev.filter(t => t.id !== id));
        } catch (e) {
            console.error("Failed to delete theme:", e);
        }
    }, []);
    
    const setActiveTheme = useCallback(async (id: string) => { 
        try {
            await axios.post(`/api/admin/themes/${id}/activate`);
            setSavedThemes(prev => {
                const newSavedThemes = prev.map(t => ({...t, isActive: t.id === id}));
                const t = newSavedThemes.find(x => x.id === id);
                if (t) {
                    // Update active theme directly outside the state update using setTimeout trick or just standard setState
                    setTimeout(() => setActiveThemeConfig(t), 0);
                }
                return newSavedThemes;
            });
        } catch (e) {
            console.error("Failed to activate theme:", e);
        }
    }, []);

    const deactivateThemes = useCallback(async () => {
        try {
            await axios.post('/api/admin/themes/deactivate-all');
            setSavedThemes(prev => prev.map(t => ({...t, isActive: false})));
            setActiveThemeConfig({
                id: 'default',
                name: 'Crystal Premium',
                fontFamily: 'Inter',
                primaryColor: '#3b82f6',
                layoutType: 'crystal',
                isActive: true
            });
        } catch (e) {
            console.error("Failed to deactivate themes:", e);
        }
    }, []);

    const triggerIndexing = useCallback((n: string, p: string) => console.log("Indexing:", n, p), []);

    const trackClick = useCallback((target: string, label?: string) => {
        if (!isAdmin) {
            sendPublicTelemetry('/api/public/track', { target, label, path: window.location.pathname });
        }
    }, [isAdmin]);

    const contextValue = useMemo(() => ({
        resolvedPageContext, navigateToEntity,
        repairData: isAdmin ? repairData : repairData.filter(b => b.isActive !== false), setRepairData, selectedBrand, setSelectedBrand, selectedModel, setSelectedModel, selectedSeries, setSelectedSeries,
        selectedRepairs, setSelectedRepairs, page, setPage: handleSetPage, cart, addToCart, updateCartQuantity, removeFromCart, isCartOpen, setIsCartOpen,
        orders, addOrder, setOrders, parts, setParts, heroSlides, setHeroSlides, promotionSettings, setPromotionSettings, blogPosts, setBlogPosts,
        appointments, setAppointments, addAppointment, quoteRequests, addQuoteRequest, buybackRequests, addBuybackRequest, footerInfo, setFooterInfo, 
        notifications, notify, dismissNotification,
        isAdmin, login, logout,
        savedThemes, activeThemeConfig, importNewTheme, updateTheme, deleteTheme, setActiveTheme, deactivateThemes, triggerIndexing, trackClick,
        refreshConfig: fetchRuntimeConfig,
        findMyModelSteps: FIND_MY_MODEL_DATA, selectedPhoneForPurchase, setSelectedPhoneForPurchase, selectedPartForPurchase, setSelectedPartForPurchase,
        selectedBlogPost, setSelectedBlogPost,
        setCart, sellPhoneStep, setSellPhoneStep, sellPhoneFormData, setSellPhoneFormData,
        sharedFormData, setSharedFormData,
        isCheckoutOpen, setIsCheckoutOpen,
        showcaseProducts, setShowcaseProducts,
        language, setLanguage, t
    }) as AppContextType, [
        resolvedPageContext, navigateToEntity, repairData, selectedBrand, selectedModel, selectedSeries, selectedRepairs, page, cart, orders, parts, heroSlides, promotionSettings, blogPosts,
        appointments, quoteRequests, buybackRequests, footerInfo, isAdmin, savedThemes, activeThemeConfig, selectedPhoneForPurchase, selectedPartForPurchase,
        selectedBlogPost, sellPhoneStep, sellPhoneFormData, sharedFormData, isCheckoutOpen, showcaseProducts,
        notifications, notify, dismissNotification,
        addToCart, updateCartQuantity, removeFromCart, addOrder, addAppointment, addQuoteRequest, addBuybackRequest, importNewTheme, deleteTheme, setActiveTheme, deactivateThemes, triggerIndexing, trackClick,
        isCartOpen, updateTheme, handleSetPage, logout, fetchRuntimeConfig,
        language, setLanguage, t
    ]);

    const renderPage = () => {
        if (!isConfigLoaded) return (
            <div className="h-screen bg-[#0B1120] flex flex-col items-center justify-center text-white">
                <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-6"></div>
                <div className="font-black tracking-widest text-[10px] uppercase animate-pulse">Synchronisation...</div>
            </div>
        );
    if (page === Page.Admin) {
      return <AdminRedirect />;
    }
        
        switch (page) {
            case Page.Home: return <HomePage />;
            case Page.Repairs: return <RepairsPage key={resolvedPageContext?.canonicalUrl} />;
            case Page.Accessories: return <AccessoriesPage />;
            case Page.ImeiVerification: return <ImeiPage />;
            case Page.Contact: return <ContactPage />;
            case Page.FindMyModel: return <FindMyModelPage />;
            case Page.Devices: return <DevicesPage />;
            case Page.BuyPhone: return <BuyPhonePage />;
            case Page.SellPhone: return <SellPhonePage />;
            case Page.ProductDetail: return <ProductDetailPage />;
            case Page.AccessoryDetail: return <AccessoryDetailPage />;
            case Page.Blog: return <BlogPage />;
            case Page.Terms: return <TermsPage />;
            case Page.LogoDesigner: return <LogoDesigner />;
            case Page.Setup: return <InstallSetup />;
            case Page.Unsubscribe: return <UnsubscribePage />;
            default: return <HomePage />;
        }
    };

    return (
        <AppContext.Provider value={contextValue}>
            <MetaTags />
            {activeThemeConfig.customCss && page !== Page.Admin && (
                <style dangerouslySetInnerHTML={{ __html: activeThemeConfig.customCss }} />
            )}
            <div className={`min-h-screen flex flex-col`} style={{ fontFamily: !activeThemeConfig.fontFamily || activeThemeConfig.fontFamily === 'Inter' ? STABLE_SYSTEM_FONT_STACK : activeThemeConfig.fontFamily === 'JetBrains Mono' ? 'JetBrains Mono, monospace' : ['Space Grotesk', 'Outfit', 'Playfair Display', 'Montserrat', 'Poppins', 'Plus Jakarta Sans'].includes(activeThemeConfig.fontFamily) ? `${activeThemeConfig.fontFamily}, ${STABLE_SYSTEM_FONT_STACK}` : activeThemeConfig.fontFamily }}>
                {page !== Page.Admin && !selectedBlogPost && <PromoBanner />}
                {page !== Page.Admin && !selectedBlogPost && <Navbar />}
                {apiError && page !== Page.Admin && (
                    <div className="bg-amber-500 text-white text-xs font-semibold py-2 px-4 flex items-center justify-between shadow-md relative z-50">
                        <div className="flex items-center gap-2 mx-auto">
                            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            <span>{apiError}</span>
                            <button 
                                onClick={() => {
                                    setApiError(null);
                                    fetchRuntimeConfig();
                                }} 
                                className="ml-3 px-2.5 py-1 bg-white text-amber-700 hover:bg-amber-50 rounded text-[10px] uppercase font-bold tracking-wider transition-all duration-150 shadow-sm"
                            >
                                Réessayer
                            </button>
                        </div>
                    </div>
                )}
                <main className="flex-grow">
                    <Suspense fallback={
                        <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center text-slate-800">
                            <div role="status" aria-label="Préparation de la page" className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
                        </div>
                    }>
                        {isConfigLoaded
                            ? <PublicPageReadyBoundary>{renderPage()}</PublicPageReadyBoundary>
                            : renderPage()}
                    </Suspense>
                </main>
                <Suspense fallback={null}>
                    {page !== Page.Admin && (isCartOpen || shouldLoadAuxiliaryUi) && <UnifiedCartDrawer />}
                    {page !== Page.Admin && <DeferredFooterInfo />}
                    {page !== Page.Admin && shouldLoadAuxiliaryUi && <AiChatbot />}
                    {page !== Page.Admin && shouldLoadAuxiliaryUi && <PromotionPopup key={page} />}
                </Suspense>
                <Suspense fallback={null}>
                    {page !== Page.Admin && shouldLoadAuxiliaryUi && <WishlistManager />}
                </Suspense>
                <Toast notifications={notifications} onDismiss={dismissNotification} />
            </div>
        </AppContext.Provider>
    );
};

export default App;
